import React, { useMemo } from 'react';

function TraditionalChart({ chartData, birthInfo }) {
  // Calculate planets in houses (1-12) using WHOLE SIGN system (traditional Vedic)
  const planetsInHouses = useMemo(() => {
    if (!chartData?.planets || !chartData?.houses?.ascendant) return {};

    const houses = {};
    for (let i = 1; i <= 12; i++) houses[i] = [];

    const ascSignIndex = chartData.houses.ascendant.signIndex;

    Object.entries(chartData.planets).forEach(([name, planet]) => {
      if (planet?.signIndex !== undefined) {
        // WHOLE SIGN: House number = (planet's sign - ascendant sign + 12) % 12 + 1
        const houseNum = ((planet.signIndex - ascSignIndex + 12) % 12) + 1;
        houses[houseNum].push({
          name,
          symbol: getPlanetAbbrev(name),
          degree: planet.degree,
          minute: planet.minute,
          isRetrograde: planet.isRetrograde
        });
      }
    });

    return houses;
  }, [chartData]);

  // Calculate D9 (Navamsa) positions
  const navamsaHouses = useMemo(() => {
    if (!chartData?.planets) return {};
    const houses = {};
    for (let i = 1; i <= 12; i++) houses[i] = [];

    Object.entries(chartData.planets).forEach(([name, planet]) => {
      if (planet?.longitude !== undefined) {
        const navamsaSign = calculateNavamsa(planet.longitude);
        const ascNavamsa = chartData.houses?.ascendant?.longitude
          ? calculateNavamsa(chartData.houses.ascendant.longitude)
          : 0;
        const houseNum = ((navamsaSign - ascNavamsa + 12) % 12) + 1;
        houses[houseNum].push({
          name,
          symbol: getPlanetAbbrev(name)
        });
      }
    });

    return houses;
  }, [chartData]);

  if (!chartData) {
    return <div className="text-white/60 text-center py-8">No chart data available</div>;
  }

  return (
    <div className="bg-white text-black w-full max-w-7xl mx-auto rounded-lg shadow-2xl mt-2">
      {/* HEADER BAR */}
      <div className="bg-slate-100 border-b-2 border-slate-800 px-4 py-3 rounded-t-lg">
        <h2 className="text-center text-base font-bold text-slate-900">Traditional Vedic Birth Chart</h2>
      </div>

      {/* BIRTH INFO TABLE */}
      <BirthInfoTable chartData={chartData} birthInfo={birthInfo} />

      {/* CHARTS ROW */}
      <div className="grid grid-cols-2 gap-2 p-4 bg-white">
        <div className="border-2 border-slate-800">
          <p className="text-center text-xs font-bold py-1 bg-slate-100 border-b border-slate-800">Lagna Chart</p>
          <NorthIndianChart housesData={planetsInHouses} ascendantSign={chartData.houses?.ascendant?.sign} />
        </div>
        <div className="border-2 border-slate-800">
          <p className="text-center text-xs font-bold py-1 bg-slate-100 border-b border-slate-800">Navamsa Chart</p>
          <NorthIndianChart housesData={navamsaHouses} ascendantSign={chartData.houses?.ascendant?.sign} isD9={true} />
        </div>
      </div>

      {/* PLANETARY POSITIONS TABLE */}
      <PlanetaryPositionsTable chartData={chartData} />

      {/* VIMSHOTTARI DASHA TABLES */}
      <VimshottariDashaTables chartData={chartData} />

      {/* ASHTAKVARGA TABLE */}
      <AshtakvargaTable chartData={chartData} />

      {/* BHAV TABLE */}
      <BhavTable chartData={chartData} />
    </div>
  );
}

// ============== BIRTH INFO TABLE ==============
function BirthInfoTable({ chartData, birthInfo }) {
  const date = new Date(`${birthInfo?.date}T${birthInfo?.time}:00`);
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const formattedDate = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  const ayanamsaDeg = chartData.ayanamsha;
  const ayanamsaFormatted = formatAyanamsha(ayanamsaDeg);

  const ascSign = chartData.houses?.ascendant?.sign || '';
  const ascLord = getSignLord(ascSign);

  // Calculate Tithi, Karan, Yoga (simplified)
  const moonLong = chartData.planets?.MOON?.longitude || 0;
  const sunLong = chartData.planets?.SUN?.longitude || 0;
  const tithiNum = Math.floor(((moonLong - sunLong + 360) % 360) / 12);
  const tithiName = getTithiName(tithiNum);
  const karanName = getKaranName(tithiNum);

  return (
    <div className="bg-white border-b-2 border-slate-800">
      <table className="w-full text-xs border-collapse">
        <tbody>
          <tr className="border-b border-slate-300">
            <td className="px-2 py-1 font-semibold border-r border-slate-300 w-1/12">Name</td>
            <td className="px-2 py-1 border-r border-slate-300 w-2/12">User</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300 w-1/12">Julian Day</td>
            <td className="px-2 py-1 border-r border-slate-300 w-2/12">{Math.floor(chartData.birthInfo?.jd || 0)}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300 w-1/12">Asc Lord</td>
            <td className="px-2 py-1 border-r border-slate-300 w-1/12">{getPlanetAbbrev(ascLord).toUpperCase()}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300 w-1/12">Bal. Dasa</td>
            <td className="px-2 py-1 w-2/12">{getDashaBalance(chartData)}</td>
          </tr>
          <tr className="border-b border-slate-300">
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Sex</td>
            <td className="px-2 py-1 border-r border-slate-300">-</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Ayan Type</td>
            <td className="px-2 py-1 border-r border-slate-300">Lahiri</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Asc</td>
            <td className="px-2 py-1 border-r border-slate-300">{ascSign}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Karan</td>
            <td className="px-2 py-1">{karanName}</td>
          </tr>
          <tr className="border-b border-slate-300">
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Date</td>
            <td className="px-2 py-1 border-r border-slate-300">{formattedDate}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Ayanamsa</td>
            <td className="px-2 py-1 border-r border-slate-300">{ayanamsaFormatted}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Yoga</td>
            <td className="px-2 py-1 border-r border-slate-300">-</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Star Lord</td>
            <td className="px-2 py-1">{getPlanetAbbrev(getNakshatraLord(chartData.planets?.MOON?.nakshatra?.name)).toUpperCase()}</td>
          </tr>
          <tr className="border-b border-slate-300">
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Day</td>
            <td className="px-2 py-1 border-r border-slate-300">{dayName}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Place</td>
            <td className="px-2 py-1 border-r border-slate-300">{birthInfo?.place || 'Unknown'}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Tithi</td>
            <td className="px-2 py-1 border-r border-slate-300">{tithiName}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Star - Pada</td>
            <td className="px-2 py-1">{chartData.planets?.MOON?.nakshatra?.name}-{chartData.planets?.MOON?.nakshatra?.pada}</td>
          </tr>
          <tr className="border-b border-slate-300">
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Time</td>
            <td className="px-2 py-1 border-r border-slate-300">{birthInfo?.time}</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Longitude</td>
            <td className="px-2 py-1 border-r border-slate-300">{birthInfo?.longitude}.E</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Sunset</td>
            <td className="px-2 py-1 border-r border-slate-300">-</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Rasi Lord</td>
            <td className="px-2 py-1">{getPlanetAbbrev(getSignLord(chartData.planets?.MOON?.sign)).toUpperCase()}</td>
          </tr>
          <tr>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">SID</td>
            <td className="px-2 py-1 border-r border-slate-300">-</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Latitude</td>
            <td className="px-2 py-1 border-r border-slate-300">{birthInfo?.latitude}.N</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Sunrise</td>
            <td className="px-2 py-1 border-r border-slate-300">-</td>
            <td className="px-2 py-1 font-semibold border-r border-slate-300">Rasi</td>
            <td className="px-2 py-1">{chartData.planets?.MOON?.sign}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ============== NORTH INDIAN CHART ==============
function NorthIndianChart({ housesData, ascendantSign, isD9 = false }) {
  // House positions in the diamond (x, y are centers in 200x200 viewBox)
  const housePositions = {
    1:  { x: 100, y: 50,  label: { x: 100, y: 35 } },   // Top center
    2:  { x: 50,  y: 25,  label: { x: 50,  y: 15 } },   // Top-left
    3:  { x: 25,  y: 50,  label: { x: 15,  y: 50 } },   // Left-top corner
    4:  { x: 50,  y: 100, label: { x: 35,  y: 100 } },  // Left center
    5:  { x: 25,  y: 150, label: { x: 15,  y: 150 } },  // Left-bottom corner
    6:  { x: 50,  y: 175, label: { x: 50,  y: 187 } },  // Bottom-left
    7:  { x: 100, y: 150, label: { x: 100, y: 167 } },  // Bottom center
    8:  { x: 150, y: 175, label: { x: 150, y: 187 } },  // Bottom-right
    9:  { x: 175, y: 150, label: { x: 187, y: 150 } },  // Right-bottom corner
    10: { x: 150, y: 100, label: { x: 165, y: 100 } },  // Right center
    11: { x: 175, y: 50,  label: { x: 187, y: 50 } },   // Right-top corner
    12: { x: 150, y: 25,  label: { x: 150, y: 15 } },   // Top-right
  };

  const renderPlanets = (houseNum, centerX, centerY) => {
    const planets = housesData[houseNum] || [];
    if (planets.length === 0) return null;

    return planets.map((p, i) => {
      const offsetY = (i - (planets.length - 1) / 2) * 8;
      return (
        <text
          key={i}
          x={centerX}
          y={centerY + offsetY}
          textAnchor="middle"
          fontSize="9"
          fontWeight="bold"
          fill="#000"
          dominantBaseline="middle"
        >
          {p.symbol}{p.isRetrograde ? '(R)' : ''}
        </text>
      );
    });
  };

  return (
    <div className="bg-white p-3 mx-auto" style={{ width: '100%', maxWidth: '350px', aspectRatio: '1/1' }}>
      <svg viewBox="0 0 200 200" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {/* Outer square */}
        <rect x="2" y="2" width="196" height="196" fill="white" stroke="black" strokeWidth="1.5" />

        {/* Diagonals */}
        <line x1="2" y1="2" x2="198" y2="198" stroke="black" strokeWidth="1" />
        <line x1="198" y1="2" x2="2" y2="198" stroke="black" strokeWidth="1" />

        {/* Inner diamond */}
        <polygon points="100,2 198,100 100,198 2,100" fill="none" stroke="black" strokeWidth="1" />

        {/* House Number Labels */}
        {Object.entries(housePositions).map(([num, pos]) => (
          <text
            key={`label-${num}`}
            x={pos.label.x}
            y={pos.label.y}
            textAnchor="middle"
            fontSize="7"
            fill="#888"
            dominantBaseline="middle"
          >
            {num}
          </text>
        ))}

        {/* Planets in each house */}
        {Object.entries(housePositions).map(([num, pos]) => (
          <g key={`planets-${num}`}>
            {renderPlanets(parseInt(num), pos.x, pos.y)}
          </g>
        ))}
      </svg>
    </div>
  );
}

// ============== PLANETARY POSITIONS TABLE ==============
function PlanetaryPositionsTable({ chartData }) {
  const planets = ['ASC', 'SUN', 'MOON', 'MERCURY', 'VENUS', 'MARS', 'JUPITER', 'SATURN', 'RAHU', 'KETU'];

  const getPlanetData = (planet) => {
    if (planet === 'ASC') {
      const a = chartData.houses?.ascendant;
      return a ? {
        sign: a.sign,
        latLong: `${pad(a.degree)}-${pad(a.minute)}-${pad(a.second)}`,
        nakshatra: getNakshatraFromLong(a.longitude),
        pada: getPadaFromLong(a.longitude)
      } : null;
    }
    const p = chartData.planets?.[planet];
    return p ? {
      sign: p.sign,
      latLong: `${pad(p.degree)}-${pad(p.minute)}-${pad(p.second)}${p.isRetrograde ? '(R)' : ''}`,
      nakshatra: p.nakshatra?.name,
      pada: p.nakshatra?.pada
    } : null;
  };

  return (
    <div className="border-b-2 border-slate-800 p-4 bg-white">
      <p className="text-center text-xs font-bold mb-2">Planetary Positions</p>
      <table className="w-full text-xs border-collapse border border-slate-800">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-400 px-2 py-1 text-left">Planets</th>
            <th className="border border-slate-400 px-2 py-1 text-left">Sign</th>
            <th className="border border-slate-400 px-2 py-1 text-left">Latitude</th>
            <th className="border border-slate-400 px-2 py-1 text-left">Nakshatra</th>
            <th className="border border-slate-400 px-2 py-1 text-left">Pada</th>
          </tr>
        </thead>
        <tbody>
          {planets.map(planet => {
            const data = getPlanetData(planet);
            if (!data) return null;
            return (
              <tr key={planet}>
                <td className="border border-slate-400 px-2 py-1 font-semibold">{planet === 'ASC' ? 'ASC' : planet.charAt(0) + planet.slice(1).toLowerCase()}</td>
                <td className="border border-slate-400 px-2 py-1">{data.sign}</td>
                <td className="border border-slate-400 px-2 py-1 font-mono">{data.latLong}</td>
                <td className="border border-slate-400 px-2 py-1">{data.nakshatra}</td>
                <td className="border border-slate-400 px-2 py-1">{data.pada}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============== VIMSHOTTARI DASHA TABLES ==============
function VimshottariDashaTables({ chartData }) {
  const dashas = chartData.dashas?.sequenceWithAntardashas || chartData.dashas?.sequence || [];

  return (
    <div className="border-b-2 border-slate-800 p-4 bg-white">
      <p className="text-center text-xs font-bold mb-2">Vimshottari Dasha</p>
      <div className="grid grid-cols-3 gap-2">
        {dashas.map((dasha, idx) => (
          <DashaTable key={idx} dasha={dasha} />
        ))}
      </div>
    </div>
  );
}

function DashaTable({ dasha }) {
  const startDate = formatShortDate(dasha.startDate);
  const endDate = formatShortDate(dasha.endDate);
  const antardashas = dasha.antardashas || [];

  return (
    <div className="border border-slate-800 text-[10px]">
      <div className="bg-slate-100 border-b border-slate-800 px-1 py-0.5 flex justify-between font-bold">
        <span>{getPlanetAbbrev(dasha.lord).toUpperCase()} -{dasha.period} Years</span>
        <span>{startDate} - {endDate}</span>
      </div>
      <table className="w-full border-collapse">
        <tbody>
          {antardashas.slice(0, 9).map((ad, i) => (
            <tr key={i} className="border-b border-slate-300">
              <td className="px-1 py-0.5 font-semibold">{getPlanetAbbrev(ad.lord).toUpperCase()}</td>
              <td className="px-1 py-0.5 text-right font-mono">{formatShortDate(ad.startDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============== ASHTAKVARGA TABLE ==============
function AshtakvargaTable({ chartData }) {
  // Simplified Ashtakvarga - just structure for now
  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Lagna'];

  // Generate sample scores (would need actual Ashtakvarga calculation)
  const generateScores = (planet) => {
    return Array.from({ length: 12 }, () => Math.floor(Math.random() * 6) + 2);
  };

  return (
    <div className="border-b-2 border-slate-800 p-4 bg-white">
      <p className="text-center text-xs font-bold mb-2">Ashtakvarga Table</p>
      <table className="w-full text-[10px] border-collapse border border-slate-800">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-400 px-1 py-0.5">Sign No</th>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
              <th key={n} className="border border-slate-400 px-1 py-0.5">{n}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {planets.map(planet => {
            const scores = generateScores(planet);
            return (
              <tr key={planet}>
                <td className="border border-slate-400 px-1 py-0.5 font-semibold">{planet}</td>
                {scores.map((s, i) => (
                  <td key={i} className="border border-slate-400 px-1 py-0.5 text-center">{s}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============== BHAV TABLE ==============
function BhavTable({ chartData }) {
  const ascDegree = chartData.houses?.ascendant?.longitude || 0;
  const ascendantSignIndex = chartData.houses?.ascendant?.signIndex || 0;
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

  const bhavs = [];
  for (let i = 0; i < 12; i++) {
    const bhavStart = (ascDegree + i * 30) % 360;
    const midBhav = (bhavStart + 15) % 360;
    const signIdx = (ascendantSignIndex + i) % 12;
    bhavs.push({
      num: i + 1,
      begin: longToDMS(bhavStart),
      sign: signs[signIdx],
      mid: longToDMS(midBhav)
    });
  }

  return (
    <div className="border-b-2 border-slate-800 p-4 bg-white">
      <p className="text-center text-xs font-bold mb-2">Bhav Table</p>
      <table className="w-full text-[10px] border-collapse border border-slate-800">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-400 px-2 py-0.5">Bhav</th>
            <th className="border border-slate-400 px-2 py-0.5">Bhav Begin</th>
            <th className="border border-slate-400 px-2 py-0.5">Sign</th>
            <th className="border border-slate-400 px-2 py-0.5">Mid Bhav</th>
          </tr>
        </thead>
        <tbody>
          {bhavs.map(b => (
            <tr key={b.num}>
              <td className="border border-slate-400 px-2 py-0.5 text-center">{b.num}</td>
              <td className="border border-slate-400 px-2 py-0.5 font-mono">{b.begin}</td>
              <td className="border border-slate-400 px-2 py-0.5">{b.sign}</td>
              <td className="border border-slate-400 px-2 py-0.5 font-mono">{b.mid}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============== HELPER FUNCTIONS ==============
function getPlanetAbbrev(name) {
  const abbrevs = {
    'SUN': 'Su', 'Sun': 'Su',
    'MOON': 'Mo', 'Moon': 'Mo',
    'MARS': 'Ma', 'Mars': 'Ma',
    'MERCURY': 'Me', 'Mercury': 'Me',
    'JUPITER': 'Ju', 'Jupiter': 'Ju',
    'VENUS': 'Ve', 'Venus': 'Ve',
    'SATURN': 'Sa', 'Saturn': 'Sa',
    'RAHU': 'Ra', 'Rahu': 'Ra',
    'KETU': 'Ke', 'Ketu': 'Ke'
  };
  return abbrevs[name] || name?.substring(0, 2) || '';
}

function getSignLord(sign) {
  const lords = {
    'Aries': 'Mars', 'Taurus': 'Venus', 'Gemini': 'Mercury',
    'Cancer': 'Moon', 'Leo': 'Sun', 'Virgo': 'Mercury',
    'Libra': 'Venus', 'Scorpio': 'Mars', 'Sagittarius': 'Jupiter',
    'Capricorn': 'Saturn', 'Aquarius': 'Saturn', 'Pisces': 'Jupiter'
  };
  return lords[sign] || '';
}

function getNakshatraLord(nakshatraName) {
  const lords = {
    'Ashwini': 'Ketu', 'Bharani': 'Venus', 'Krittika': 'Sun',
    'Rohini': 'Moon', 'Mrigashirsha': 'Mars', 'Ardra': 'Rahu',
    'Punarvasu': 'Jupiter', 'Pushya': 'Saturn', 'Ashlesha': 'Mercury',
    'Magha': 'Ketu', 'Purva Phalguni': 'Venus', 'Uttara Phalguni': 'Sun',
    'Hasta': 'Moon', 'Chitra': 'Mars', 'Swati': 'Rahu',
    'Vishakha': 'Jupiter', 'Anuradha': 'Saturn', 'Jyeshtha': 'Mercury',
    'Mula': 'Ketu', 'Purva Ashadha': 'Venus', 'Uttara Ashadha': 'Sun',
    'Shravana': 'Moon', 'Dhanishtha': 'Mars', 'Shatabhisha': 'Rahu',
    'Purva Bhadrapada': 'Jupiter', 'Uttara Bhadrapada': 'Saturn', 'Revati': 'Mercury'
  };
  return lords[nakshatraName] || '';
}

function getTithiName(num) {
  const tithis = ['Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
                  'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
                  'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima'];
  return tithis[num % 15] || '';
}

function getKaranName(tithiNum) {
  const karans = ['Bava', 'Balava', 'Kaulav', 'Taitila', 'Garaja', 'Vanija', 'Vishti'];
  return karans[tithiNum % 7] || '';
}

function getDashaBalance(chartData) {
  const dashas = chartData.dashas;
  if (!dashas?.sequence?.[dashas.currentDashaIndex]) return '-';
  const current = dashas.sequence[dashas.currentDashaIndex];
  const lord = getPlanetAbbrev(current.lord);
  const balance = dashas.remainingBalance || 0;
  const years = Math.floor(balance);
  const months = Math.floor((balance - years) * 12);
  const days = Math.floor(((balance - years) * 12 - months) * 30);
  return `${lord} ${years} Y ${months} M ${days} D`;
}

function calculateNavamsa(longitude) {
  // D9 - Each sign is divided into 9 parts of 3°20' each
  const signIdx = Math.floor(longitude / 30);
  const degInSign = longitude % 30;
  const navamsaInSign = Math.floor(degInSign / (30 / 9));

  // Navamsa starts based on sign type
  let startSign;
  if (signIdx % 3 === 0) startSign = signIdx; // Movable: own sign
  else if (signIdx % 3 === 1) startSign = (signIdx + 8) % 12; // Fixed: 9th from itself
  else startSign = (signIdx + 4) % 12; // Dual: 5th from itself

  return (startSign + navamsaInSign) % 12;
}

function getNakshatraFromLong(longitude) {
  const nakshatras = ['Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashirsha', 'Ardra',
                      'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
                      'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
                      'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishtha',
                      'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'];
  const idx = Math.floor(longitude / (800 / 60));
  return nakshatras[idx % 27];
}

function getPadaFromLong(longitude) {
  const nakSize = 800 / 60;
  const nakLong = longitude % nakSize;
  return Math.floor(nakLong / (nakSize / 4)) + 1;
}

function pad(n) {
  return String(n || 0).padStart(2, '0');
}

function formatAyanamsha(deg) {
  if (!deg) return '00-00-00';
  const d = Math.floor(deg);
  const m = Math.floor((deg - d) * 60);
  const s = Math.floor(((deg - d) * 60 - m) * 60);
  return `${pad(d).padStart(3, '0')}-${pad(m)}-${pad(s)}`;
}

function longToDMS(longitude) {
  longitude = longitude % 30;
  const d = Math.floor(longitude);
  const m = Math.floor((longitude - d) * 60);
  const s = Math.floor(((longitude - d) * 60 - m) * 60);
  return `${pad(d)}.${pad(m)}.${pad(s)}`;
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
}

export default TraditionalChart;
